import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'portfolio-selected-facilities';

/**
 * 基于 companyId 管理选中设施的 hook
 * @param {string|number} companyId - 公司ID
 * @returns {[Set, function, function]} - [selectedFacilities, setSelectedFacilities, hasCache]
 */
const useSelectedFacilitiesStorageByCompanyId = (companyId) => {
    // 从 localStorage 读取数据的函数
    const getStorageData = useCallback(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Failed to parse localStorage data:', error);
            return {};
        }
    }, []);

    // 保存数据到 localStorage 的函数
    const saveStorageData = useCallback((data) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save to localStorage:', error);
        }
    }, []);

    // 初始化状态
    const [selectedFacilities, setSelectedFacilitiesState] = useState(() => {
        if (!companyId) return new Set();
        
        const storageData = getStorageData();
        const facilityIds = storageData[companyId] || [];
        return new Set(facilityIds);
    });

    // 当 companyId 变化时，更新选中状态
    useEffect(() => {
        if (!companyId) {
            setSelectedFacilitiesState(new Set());
            return;
        }

        const storageData = getStorageData();
        const facilityIds = storageData[companyId] || [];
        setSelectedFacilitiesState(new Set(facilityIds));
    }, [companyId, getStorageData]);

    // 设置选中设施的函数
    const setSelectedFacilities = useCallback((newSelectedFacilities) => {
        if (!companyId) return;

        // 支持函数式更新
        const updatedSet = typeof newSelectedFacilities === 'function' 
            ? newSelectedFacilities(selectedFacilities)
            : newSelectedFacilities;

        // 更新状态
        setSelectedFacilitiesState(updatedSet);

        // 更新 localStorage
        const storageData = getStorageData();
        storageData[companyId] = Array.from(updatedSet);
        saveStorageData(storageData);
    }, [companyId, selectedFacilities, getStorageData, saveStorageData]);

    // 检查指定 companyId 是否有缓存数据的函数
    const hasCache = useCallback((targetCompanyId) => {
        if (!targetCompanyId) return false;
        
        const storageData = getStorageData();
        return storageData[targetCompanyId] !== undefined
    }, [getStorageData]);

    return [selectedFacilities, setSelectedFacilities, hasCache];
};

export default useSelectedFacilitiesStorageByCompanyId; 